/* Вопрос ОСНОВА.Эксперту с ответом потоком (SSE): страница печатает ответ по
   мере генерации, как ChatGPT. EventSource не умеет POST с файлами, поэтому —
   fetch + чтение тела по кускам, как в `chatStream.ts`.

   События сервера (`/agents/progress/advisor/ask/stream`): `delta` — кусок
   текста, `status` — «ищу в интернете» / «готовлю файл» / «смотрю в ОСНОВЕ: …»,
   `reset` — напечатанное сбросить,
   `done` — итог (как ответ `/ask`), `error` — ошибка в виде тела HTTP-ошибки.

   Ошибки бросаются в форме ошибки RTK Query (`{ status, data }`), чтобы экран
   разбирал их тем же кодом, что и обычные. Токен — из общего store; на 401 —
   один refresh и повтор (общий мьютекс с baseQuery). */

import { store } from '@app/store';
import type { AppDispatch, RootState } from '@app/store';
import { refreshAccessToken } from '@features/auth/model/refresh';
import { clearTokens } from '@features/auth/model/slice';
import type { AdvisorAnswer, AdvisorAskInput } from '@entities/progress/advisor';
import { progressApi } from './endpoints/progress';

const API_BASE: string = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';
const URL_ASK_STREAM = `${API_BASE}/agents/progress/advisor/ask/stream`;

export interface AdvisorStreamHandlers {
  /** Очередной кусок текста ответа. */
  onDelta: (text: string) => void;
  /** Модель ищет в интернете. */
  onSearching: () => void;
  /** Модель пишет и запускает код — готовит файл или считает. */
  onCoding: () => void;
  /** Модель смотрит данные ОСНОВЫ: что именно — «поиск по документам», «сводка по объекту»… */
  onTool: (label: string) => void;
  /** Напечатанное сбросить — ответ начнётся заново. */
  onReset: () => void;
}

/** Ошибка в форме RTK Query: её понимают `apiErrorMessage` и экран Эксперта. */
export interface AdvisorStreamError {
  status: number | 'FETCH_ERROR' | 'ABORTED';
  data?: unknown;
}

function formData({ session_id, question, files }: AdvisorAskInput): FormData {
  const fd = new FormData();
  fd.append('question', question);
  if (session_id) fd.append('session_id', session_id);
  for (const f of files) fd.append('files', f, f.name);
  return fd;
}

async function post(input: AdvisorAskInput, signal: AbortSignal | undefined): Promise<Response> {
  const token = (store.getState() as RootState).auth.accessToken;
  try {
    return await fetch(URL_ASK_STREAM, {
      method: 'POST',
      headers: {
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      // Тело собираем заново на каждый запрос: FormData с файлами одноразовая.
      body: formData(input),
      signal,
    });
  } catch {
    throw { status: signal?.aborted ? 'ABORTED' : 'FETCH_ERROR' } satisfies AdvisorStreamError;
  }
}

/** Одно событие SSE: имя и JSON из строк `data:`; комментарии keep-alive — `null`. */
function parseBlock(block: string): { event: string; data: unknown } | null {
  let event = 'message';
  const lines: string[] = [];
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) lines.push(line.slice(5).replace(/^ /, ''));
  }
  if (lines.length === 0) return null;
  try {
    return { event, data: JSON.parse(lines.join('\n')) };
  } catch {
    return null;
  }
}

/**
 * Задать вопрос и получить ответ потоком. Куски — в `handlers`, итог —
 * результатом промиса (в кэш его кладёт {@link storeAdvisorAnswer}, когда
 * экран допечатает ответ).
 *
 * @throws {AdvisorStreamError} сбой сети, HTTP-ошибка до начала потока или
 *   `error` в потоке; `ABORTED` — запрос отменён (ушли со страницы).
 */
export async function askAdvisorStream(
  input: AdvisorAskInput,
  handlers: AdvisorStreamHandlers,
  signal?: AbortSignal,
): Promise<AdvisorAnswer> {
  let resp = await post(input, signal);
  if (resp.status === 401) {
    const ok = await refreshAccessToken(
      store.dispatch as AppDispatch,
      store.getState as () => RootState,
    );
    if (!ok) {
      store.dispatch(clearTokens());
      throw { status: 401 } satisfies AdvisorStreamError;
    }
    resp = await post(input, signal);
  }
  if (!resp.ok || !resp.body) {
    const data: unknown = await resp.json().catch(() => undefined);
    throw { status: resp.status, data } satisfies AdvisorStreamError;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  try {
    for (;;) {
      let chunk: ReadableStreamReadResult<Uint8Array>;
      try {
        chunk = await reader.read();
      } catch {
        throw { status: signal?.aborted ? 'ABORTED' : 'FETCH_ERROR' } satisfies AdvisorStreamError;
      }
      if (chunk.done) break;
      buffer += decoder.decode(chunk.value, { stream: true }).replace(/\r/g, '');
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const ev = parseBlock(buffer.slice(0, sep));
        buffer = buffer.slice(sep + 2);
        if (!ev) continue;
        const data = ev.data as Record<string, unknown>;
        switch (ev.event) {
          case 'delta':
            if (typeof data.delta === 'string' && data.delta) handlers.onDelta(data.delta);
            break;
          case 'status':
            if (data.state === 'searching') handlers.onSearching();
            else if (data.state === 'coding') handlers.onCoding();
            else if (data.state === 'tool') {
              handlers.onTool(typeof data.label === 'string' ? data.label : 'данные');
            }
            break;
          case 'reset':
            handlers.onReset();
            break;
          case 'done':
            return data as unknown as AdvisorAnswer;
          case 'error':
            throw {
              status: typeof data.status === 'number' ? data.status : 500,
              data,
            } satisfies AdvisorStreamError;
        }
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // поток уже закрыт
    }
  }
  // Поток кончился без итога — связь оборвалась посреди ответа.
  throw { status: 'FETCH_ERROR' } satisfies AdvisorStreamError;
}

/**
 * Готовый ответ — в кэш: реплики чата, расход месяца, список чатов. Новый чат
 * (`askedSession` = null) кладётся готовым, без загрузки.
 */
export function storeAdvisorAnswer(
  dispatch: AppDispatch,
  askedSession: string | null,
  data: AdvisorAnswer,
): void {
  const pair = [data.question, data.answer];
  if (!askedSession) {
    void dispatch(progressApi.util.upsertQueryData('listAdvisorMessages', data.session.id, pair));
  } else {
    dispatch(
      progressApi.util.updateQueryData('listAdvisorMessages', data.session.id, (draft) => {
        if (!draft.some((m) => m.id === data.answer.id)) draft.push(...pair);
      }),
    );
  }
  void dispatch(progressApi.util.upsertQueryData('getAdvisorUsage', undefined, data.usage));
  dispatch(progressApi.util.invalidateTags(['AdvisorSession']));
}

/** Перечитать реплики чата с сервера (страховка, если кэш не обновился). */
export function reloadAdvisorChat(dispatch: AppDispatch, sessionId: string): void {
  dispatch(progressApi.util.invalidateTags([{ type: 'AdvisorMessages', id: sessionId }]));
}
